//src\components\portal\next_update.js
import React, { useState, useEffect } from 'react';
import { useTheme, ThemeProvider } from '@mui/material/styles';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Container,
  Grid
} from '@mui/material';
import portalSession from '../portal_services/portal_session'; 



const NextUpdateInfo = () => {
  const theme = useTheme();
  const [updateData, setUpdateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // API configuration
  const BASE_URL = portalSession.getBaseURL();
  const CUSTOMER_ID = portalSession.getCustomerId();
  const CUSTOMER_TOKEN = portalSession.getCustomerToken();

  useEffect(() => {
    const fetchUpdateData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/find_updates?timeframe=7d&customer_name=canterbury_basketball`, {
          method: 'GET',
          headers: {
            'X-Customer-ID': CUSTOMER_ID,
            'X-Customer-Token': CUSTOMER_TOKEN,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setUpdateData(data);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching update data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUpdateData();
  }, []);

  // Helper function to calculate statistics
  const calculateStats = () => {
    if (!updateData?.linkOperations) {
      return {
        totalOperations: 0,
        docCount: 0,
        siteCount: 0,
        addCount: 0,
        removeCount: 0
      };
    }

    const operations = updateData.linkOperations;
    
    return {
      totalOperations: operations.length,
      docCount: operations.filter(op => op.type === 'doc').length,
      siteCount: operations.filter(op => op.type === 'site').length,
      addCount: operations.filter(op => op.operation === 'add').length,
      removeCount: operations.filter(op => op.operation === 'remove').length
    };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress color="primary" />
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Alert severity="error">
            Error loading next update data: {error}
          </Alert>
        </Container>
      </ThemeProvider>
    );
  }

  // Stats card component for reusability
  const StatsCard = ({ title, value, color = '#009688' }) => (
    <Card 
      elevation={2}
      sx={{ 
        backgroundColor: 'white',
        border: `2px solid ${color}`,
        borderRadius: 2,
        height: '100%'
      }}
    >
      <CardContent sx={{ textAlign: 'center', p: 2 }}>
        <Typography 
          variant="h3" 
          component="div"
          sx={{ 
            color: color,
            fontWeight: 'bold',
            mb: 1
          }}
        >
          {value}
        </Typography>
        <Typography 
          variant="h6" 
          component="div"
          sx={{ 
            color: '#666',
            fontWeight: 'medium'
          }}
        >
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Card 
          elevation={3}
          sx={{ 
            backgroundColor: 'white',
            border: '2px solid #009688',
            borderRadius: 2,
            mb: 3
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ 
                color: '#009688',
                fontWeight: 'bold',
                textAlign: 'center',
                mb: 3
              }}
            >
              Next Update Changes
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                textAlign: 'center',
                color: '#666',
                mb: 3
              }}
            >
              Changes scheduled for the next data update (7 days)
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <StatsCard 
                  title="Total Operations" 
                  value={stats.totalOperations}
                  color="#009688"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <StatsCard 
                  title="Document Changes" 
                  value={stats.docCount}
                  color="#4CAF50"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <StatsCard 
                  title="Site Changes" 
                  value={stats.siteCount}
                  color="#FF9800"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={6}>
                <StatsCard 
                  title="Additions" 
                  value={stats.addCount}
                  color="#2196F3"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={6}>
                <StatsCard 
                  title="Removals" 
                  value={stats.removeCount}
                  color="#F44336"
                />
              </Grid>
            </Grid>

            {updateData?.customerName && (
              <Box sx={{ mt: 3 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    textAlign: 'center',
                    color: '#666',
                    fontStyle: 'italic'
                  }}
                >
                  Customer: {updateData.customerName}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </ThemeProvider>
  );
};

export default NextUpdateInfo;